import { ArrowLeft, Download } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader } from "@/components/common/loader";
import type { ReportFilter } from "../types";

// Radix Select can't hold an empty-string value, so an "All" option (param "")
// is represented by this sentinel in the widget and translated back at the edge.
const ALL_SENTINEL = "__all__";

interface ReportScaffoldProps {
  title: string;
  description?: string;
  note?: string;
  filters?: ReportFilter[];
  filterValues: Record<string, string>;
  onFilterChange: (param: string, value: string) => void;
  onExport: () => void;
  exporting?: boolean;
  loading?: boolean;
  error?: string | null;
  /** Extra content shown in the header band (links, thresholds). */
  headerExtra?: ReactNode;
  children: ReactNode;
}

/** Shared shell for every report page: title bar, filter controls, export
 * button, and a card that holds the (sortable) table / body. */
export function ReportScaffold({
  title,
  description,
  note,
  filters,
  filterValues,
  onFilterChange,
  onExport,
  exporting,
  loading,
  error,
  headerExtra,
  children,
}: ReportScaffoldProps) {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <Link
          to="/reports"
          className="text-muted-foreground hover:text-primary inline-flex w-fit items-center gap-1 text-sm"
        >
          <ArrowLeft className="size-3.5" /> All reports
        </Link>
        <h1 className="text-2xl font-bold">{title}</h1>
        {description && <p className="text-muted-foreground text-sm">{description}</p>}
        {note && <p className="text-muted-foreground text-xs italic">{note}</p>}
        {headerExtra}
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          {filters?.map((filter) => (
            <div key={filter.param} className="grid gap-1.5">
              <span className="text-muted-foreground text-xs font-medium">{filter.label}</span>
              <Select
                value={(filterValues[filter.param] ?? filter.default) || ALL_SENTINEL}
                onValueChange={(v) =>
                  onFilterChange(filter.param, v === ALL_SENTINEL ? "" : v)
                }
              >
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {filter.options.map((opt) => (
                    <SelectItem key={opt.value || ALL_SENTINEL} value={opt.value || ALL_SENTINEL}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={onExport} disabled={exporting || loading}>
          <Download className="size-4" />
          {exporting ? "Exporting…" : "Export to Excel"}
        </Button>
      </div>

      <Card className="gap-0 overflow-hidden p-0">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader />
          </div>
        ) : error ? (
          <p className="text-destructive py-16 text-center text-sm">{error}</p>
        ) : (
          children
        )}
      </Card>
    </div>
  );
}
